/* eslint-disable no-else-return */
import {
  createRecord,
  deleteRecord,
  getRecord,
  getRecords,
  getRecordsUsingIndex,
  removeAttributesFromRecord,
  updateRecord,
} from '../services/db';
import {
  basePatch,
  basePost,
  extractUrlSegment,
  getQueryParameters,
  uuidRegex,
} from './base';
import { clearBubbleMeasurements } from './bubble';
import { clearQuestionsMeasurements } from './question';
import getCurrentEpoch from '../helpers/DateTimeHelper';

const offlineGet = async (...args) => {
  const url = args[0];

  if (url === '/job') {
    const jobs = await getRecords('job');
    return { jobs };
  }

  const jobId = extractUrlSegment(url, 1); // Extracts the second segment (job ID)
  const subPath = extractUrlSegment(url, 2); // Extracts the third segment if available

  if (jobId) {
    if (subPath === 'ra') {
      const job = await getRecord('job', jobId);
      return job?.repairAssessment;
    }

    const jobIdClean = jobId.split('?')[0]; // Handle potential query params
    const job = await getRecord('job', jobIdClean);
    return job;
  }

  throw new Error(`Failed to handle ${url} with method GET`);
};

const offlinePost = async (...args) => {
  const url = args[0];
  const body = args[1];
  const splittedUrl = url.split('/');
  if (url === '/job') {
    return basePost(args);
  } else if (extractUrlSegment(url, 2) === 'duplicate') {
    const id = splittedUrl[2];
    const data = body;

    const job = await getRecord('job', id);

    Object.keys(data).forEach((key) => {
      job[key] = data[key];
    });

    job.approvalStatus = 'initial';
    job.isNew = true;

    if (job.type && job.type === 'workOrderLibrary') {
      job.type = 'workViewerLibrary';
    }

    const tasks = await getRecordsUsingIndex('task', 'jobId', id);
    const user = await getRecord('session', 'currentUser');

    tasks.forEach(async (task) => {
      const originalTaskId = task._id;

      task.jobId = job._id;
      task.name = job.name;
      task.partNumber = job.partNumber;

      const duplicateTransactions = [];

      if (task.taskType === 'diagram') {
        const bubbles = await getRecordsUsingIndex(
          'bubble',
          'taskId',
          originalTaskId
        );
        bubbles.forEach((bubble) => {
          bubble.taskId = task._id;
          bubble.lastModifiedBy = user.fullname;
          if (task.isDuplicated) {
            clearBubbleMeasurements(bubble);
          }
          duplicateTransactions.push(createRecord('bubble', bubble));
        });
      } else if (task.taskType === 'instruction') {
        const questions = await getRecordsUsingIndex(
          'question',
          'taskId',
          originalTaskId
        );

        questions.forEach((question) => {
          question.taskId = task._id;
          question.lastModifiedBy = user.fullname;
          if (task.isDuplicated) {
            clearQuestionsMeasurements(question);
          }
          duplicateTransactions.push(createRecord('question', question));
        });
      }

      await Promise.all(duplicateTransactions);

      if (task.isDuplicated) {
        task.taskComment = '';
        task.completenessPercentage = 0;
        delete task.isDuplicated;
      }

      await createRecord('task', task);
    });

    createRecord('job', job);

    return { newJob: job };
  } else if (extractUrlSegment(url, 2) === 'ra') {
    const jobId = splittedUrl[2];

    const job = await getRecord('job', jobId);

    const { replace, replaceMinor } = body;
    if (replace) {
      if (replaceMinor) {
        const newrat = job.repairAssessment.filter((item) => !item.isMinor);
        job.repairAssessment = newrat;
      } else {
        const newrat = job.repairAssessment.filter((item) => item.isMinor);
        job.repairAssessment = newrat;
      }
    }

    let rows = [];
    if (Array.isArray(body.rows)) {
      rows = body.rows;
    } else {
      rows.push(body.rows);
    }

    const nowEpoch = getCurrentEpoch();

    if (!job.repairAssessment) {
      job.repairAssessment = [];
    }

    const { repairAssessment } = job;

    const { tool } = getQueryParameters(url);

    const user = await getRecord('session', 'user');

    rows.forEach((row) => {
      repairAssessment.push(row);

      // If the user is publishing, set the original values
      if (tool === 'publisher') {
        row.originalAssembly = row.assembly;
        row.originalItemNumber = row.itemNumber;
        row.originalPartName = row.partName;
        row.originalPartNumber = row.partNumber;
        row.originalQuantity = row.quantity;
      }

      // Create the change log
      row.changeLog = [
        {
          by: user.fullname,
          at: nowEpoch,
          tool,
        },
      ];
    });

    await updateRecord('job', { ...job, _id: jobId });

    if (rows.length === 1) {
      return rows[0];
    }
    return rows;
  } else if (
    extractUrlSegment(url, 0) === 'report' &&
    extractUrlSegment(url, 0) === 'update-attachment-image'
  ) {
    const bubbleId = splittedUrl[2];
    const { imageUrl, imageName, imgDescription } = body;

    const bubble = await getRecord('bubble', bubbleId);

    bubble.images.forEach((image) => {
      if (image.url === imageUrl) {
        if (imageName !== undefined) image.name = imageName;
        if (imgDescription !== undefined) image.description = imgDescription;
      }
    });

    const user = await getRecord('session', 'user');

    updateRecord('bubble', {
      ...bubble,
      _id: bubbleId,
      lastModifiedBy: user.fullname,
      lastModifiedAt: new Date().getTime(),
    });

    return 'ok';
  } else if (url === '/joblist') {
    // TODO: Check for filters if needed in offline mode
    const jobs = await getRecords('job');
    return { jobs };
  }
  throw new Error(`Failed to handle ${url} with method POST`);
};

const offlinePut = async (...args) => {
  const url = args[0];
  const body = args[1];
  const splittedUrl = url.split('/');
  const jobId = extractUrlSegment(url, 1);
  if (extractUrlSegment(url, 0) === 'job' && jobId) {
    const job = body;
    const updatedJob = updateRecord('job', { ...job, _id: jobId });
    return updatedJob;
  } else if (new RegExp(`^/job/${uuidRegex}/favoritecount`).test(url)) {
    const favoriteCount = body.count;
    const id = splittedUrl[2];

    updateRecord('job', { _id: id, favoriteCount });
    return { status: true, count: favoriteCount };
  } else if (new RegExp(`^/job/${uuidRegex}/undelete`).test(url)) {
    const id = splittedUrl[2];

    const deletedTasks = await getRecordsUsingIndex('task', 'jobId', id).then(
      (tasks) => tasks.filter((task) => task.deleted)
    );

    deletedTasks.forEach(async (deletedTask) => {
      await removeAttributesFromRecord('task', deletedTask._id, [
        'deleted',
        'deletedBy',
        'deletedAt',
        'folderId',
      ]);
    });
    await removeAttributesFromRecord('job', id, [
      'deleted',
      'deletedBy',
      'deletedAt',
    ]);
    return { status: true };
  } else if (new RegExp(`^/job/${uuidRegex}/ra/${uuidRegex}`).test(url)) {
    const rowId = splittedUrl[4];

    const job = getRecord('job', jobId);

    const { repairAssessment } = job;
    const rowIndex = repairAssessment.findIndex((r) => r.id === rowId);

    const row = body;
    repairAssessment[rowIndex] = row;

    updateRecord('job', { repairAssessment, _id: jobId });

    return row;
  } else if (
    new RegExp(`^/job/${uuidRegex}/ra/${uuidRegex}/${uuidRegex}`).test(url)
  ) {
    const firstRowId = splittedUrl[4];
    const secondRowId = splittedUrl[5];
    const job = await getRecord('job', jobId);

    const { repairAssessment } = job;
    const firstRowIndex = repairAssessment.findIndex(
      (r) => r.id === firstRowId
    );
    const secondRowIndex = repairAssessment.findIndex(
      (r) => r.id === secondRowId
    );

    const rowToMove = repairAssessment[firstRowIndex];
    repairAssessment.splice(firstRowIndex, 1);
    repairAssessment.splice(secondRowIndex, 0, rowToMove);

    updateRecord('job', { repairAssessment, _id: jobId });

    return repairAssessment;
  }

  throw new Error(`Failed to handle ${url} with method PUT`);
};

const offlinePatch = async (...args) => {
  const url = args[0];
  if (new RegExp(`^/job/${uuidRegex}`).test(url)) {
    basePatch(url);
  }

  throw new Error(`Failed to handle ${url} with method PATCH`);
};

const offlineDelete = async (...args) => {
  const url = args[0];
  const body = args[1];
  const splittedUrl = url.split('/');
  if (new RegExp(`^/job/${uuidRegex}`).test(url)) {
    const id = splittedUrl[2];

    const tasksToBeDeleted = await getRecordsUsingIndex('task', 'jobId', id);

    const user = await getRecord('session', 'user');

    tasksToBeDeleted.forEach(async (taskToBeDeleted) => {
      await updateRecord('task', {
        _id: taskToBeDeleted._id,
        deleted: true,
        deletedBy: user.fullname,
        deletedAt: getCurrentEpoch(),
      });
    });
    await updateRecord('job', {
      _id: id,
      deleted: true,
      deletedBy: user.fullname,
      deletedAt: getCurrentEpoch(),
    });
    return { status: true };
  } else if (new RegExp(`^/job/${uuidRegex}/ra/${uuidRegex}`).test(url)) {
    const jobId = splittedUrl[2];
    const rowId = splittedUrl[4];

    const job = await getRecord('job', jobId);

    const { repairAssessment } = job;
    const rowIndex = repairAssessment.findIndex((r) => r.id === rowId);

    const row = body;

    if (repairAssessment[rowIndex].bubbleId) {
      await deleteRecord('bubble', repairAssessment[rowIndex].bubbleId);
    }

    delete repairAssessment[rowIndex];

    await updateRecord('job', { repairAssessment, _id: jobId });

    return row;
  } else if (new RegExp(`^/job/${uuidRegex}/ra`).test(url)) {
    const jobId = splittedUrl[2];

    const job = await getRecord('job', jobId);

    const { repairAssessment } = job;

    job.repairAssessment = [];

    updateRecord('job', { ...job, _id: jobId });

    return repairAssessment;
  }

  throw new Error(`Failed to handle ${url} with method DELETE`);
};

export { offlineGet, offlinePost, offlinePut, offlinePatch, offlineDelete };
